/**
 * Первый экран главной: заголовок, кнопка в планировщик и живой чертёж справа,
 * который сам рисуется и поднимается в 3D (`HeroPlan`).
 *
 * Заголовок — единственный `h1` страницы, в нём главные слова для поисковиков.
 */
import "./landing.css";
import { Link } from "react-router-dom";
import { routerPaths } from "@/app/routerPaths";
import { AnalyticsGoal, trackGoal } from "@/shared/lib/analytics";
import { UIKit } from "@/shared/ui";
import { HeroPlan } from "./HeroPlan";

const FACTS = [
	{ value: "1 мм", label: "точность ввода" },
	{ value: "250+", label: "предметов в каталоге" },
	{ value: "3 вида", label: "план, развёртки и 3D" },
];

const rise = (seconds: number) => ({ "--d": `${seconds}s` }) as React.CSSProperties;

export const HeroSection = () => (
	<section
		// Первый экран виден сразу, не дожидаясь JS: в пре-рендере он уже «показан»
		data-reveal
		data-shown=""
		aria-labelledby="lp-hero-title"
		className="lp-grid-dark relative overflow-hidden text-landing-text"
	>
		{/* Мягкая подсветка за чертежом */}
		<div aria-hidden="true" className="lp-hero-glow" />

		{/* На телефоне: текст, чертёж, цифры — чертёж виден сразу; на широком чертёж справа */}
		<div className="relative mx-auto grid max-w-7xl gap-10 px-4 pt-10 pb-14 sm:px-8 lg:grid-cols-landing-hero lg:gap-x-10 lg:gap-y-12 lg:pt-20 lg:pb-24">
			<div className="lg:col-start-1 lg:row-start-1 lg:self-end">
				<div className="lp-rise flex items-center gap-3" style={rise(0)}>
					<div aria-hidden="true" className="h-px w-8 bg-landing-text/30" />
					<UIKit.Text as="span" className="lp-mono text-xs text-landing-muted">
						Планировщик ремонта · онлайн
					</UIKit.Text>
				</div>

				<h1 id="lp-hero-title" className="lp-display mt-6 text-landing-hero-heading">
					<UIKit.Text as="span" className="lp-rise block" style={rise(0.08)}>
						Чертёж,
					</UIKit.Text>{" "}
					{/* Пробелы между строками — для роботов: без CSS строки иначе слипаются в одно слово */}
					<UIKit.Text as="span" className="lp-rise lp-outline-light block" style={rise(0.16)}>
						развёртки
					</UIKit.Text>{" "}
					<UIKit.Text as="span" className="lp-rise block text-landing-accent" style={rise(0.24)}>
						и 3D
					</UIKit.Text>{" "}
					{/* Хвост заголовка обычным шрифтом: для кого — и ключевые слова для поисковиков */}
					<UIKit.Text
						as="span"
						className="lp-rise mt-5 block text-landing-hero-subtitle font-light tracking-normal text-landing-muted normal-case"
						style={rise(0.32)}
					>
						для любого помещения
					</UIKit.Text>
				</h1>

				<UIKit.Text
					className="lp-rise mt-7 max-w-130 text-base leading-relaxed text-landing-muted sm:text-lg"
					style={rise(0.45)}
				>
					Начертите стены, расставьте двери, розетки и мебель. Размеры, площади, развёртки стен с
					электрикой и 3D-вид соберутся сами - прямо в браузере, без установки.
				</UIKit.Text>

				<div className="lp-rise mt-9 flex flex-wrap gap-3" style={rise(0.55)}>
					<Link
						to={routerPaths.planner}
						onClick={() => trackGoal(AnalyticsGoal.PlannerCtaClicked)}
						className="lp-button w-full bg-surface text-ink hover:bg-landing-button-hover sm:w-auto"
					>
						Начать чертёж
						<UIKit.Text as="span" aria-hidden="true" className="lp-button-arrow">
							→
						</UIKit.Text>
					</Link>
					<a
						href="#how"
						className="lp-button w-full border border-landing-text/20 text-landing-text hover:border-landing-text/50 sm:w-auto"
					>
						Как это работает
					</a>
				</div>
			</div>

			<div
				className="lp-rise lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center"
				style={rise(0.1)}
			>
				<HeroPlan />
			</div>

			<dl
				className="lp-rise grid max-w-130 grid-cols-3 gap-4 border-t border-landing-text/10 pt-6 lg:col-start-1 lg:row-start-2 lg:self-start"
				style={rise(0.65)}
			>
				{FACTS.map((fact) => (
					<div key={fact.label}>
						<dt className="lp-mono text-xs leading-copy text-landing-muted">{fact.label}</dt>
						<dd className="lp-display mt-2 text-landing-stat text-landing-text">{fact.value}</dd>
					</div>
				))}
			</dl>
		</div>
	</section>
);
